import { Injectable } from '@angular/core';
import moment from 'moment/moment';

@Injectable({
    providedIn: 'root',
})
export class DateUtilsService {
    /**
     * Returns the ISO-8601 week number for the given date.
     * @param date A JavaScript Date object
     * @returns The ISO week number (1-53)
     */
    private getISOWeek(date: Date): number {
        // Copy date so we don't modify the original
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        const dayNum: number = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        // Get first day of year
        const yearStart: Date = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        // Calculate full weeks to nearest Thursday
        return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    }

    private getISOWeekYear(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum: number = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        return d.getUTCFullYear();
    }

    public formatDate(date: Date, userDateFormat: string): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return userDateFormat
            .replace('YYYY', '' + year)
            .replace('YY', ('' + year).substring(2))
            .replace('MM', '' + month)
            .replace('DD', '' + day);
    }

    public formatWeek(date: Date): string {
        return this.formatWeekByValues(this.getISOWeekYear(date), this.getISOWeek(date));
    }

    public formatWeekByValues(year: string | number, week: string | number): string {
        return `${year}/${week}`;
    }

    public formatMonth(date: Date, userDateFormat: string): string {
        const userMonthFormat = this.toMonthFormat(userDateFormat);
        return moment(date).format(userMonthFormat);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${year}-${month}`;
    }

    // TODO Having a getter getMonthFormat() at the current user class - right next to getDateFormat() would be nicer
    //      than internally calling this method in the formatMonth() method.
    /**
     * This method converts a date format (day, month, year) to a month (month, year).
     * To achieve this, it removes the date markers 'dd' including the date separator from the format string.
     *
     * @param userDateFormat A date format like 'YYYY-MM-DD'
     * @returns A month format like 'YYYY-MM'
     */
    public toMonthFormat(userDateFormat: string): string {
        const dayPos = userDateFormat.toLowerCase().indexOf('dd');
        if (dayPos === -1) {
            return userDateFormat;
        }
        if (dayPos === 0) {
            // case: dd.mm.yyyy
            return userDateFormat.substring(3, userDateFormat.length);
        } else if (dayPos === userDateFormat.length - 2) {
            // case: YYYY-MM-DD
            return userDateFormat.substring(0, userDateFormat.length - 3);
        } else {
            // case: MM/DD/YYYY
            const before = userDateFormat.substring(0, dayPos);
            const after = userDateFormat.substring(dayPos + 3, userDateFormat.length);
            return before + after;
        }
    }

    public formatYear(date: Date): string {
        return '' + date.getFullYear();
    }

    /**
     * This method converts an ISO-8601 week date string to a JS date object.
     * @param isoWeekDate A string representing an ISO-8601 week date string with and without days ("YYYY-Www" or "YYYY-Www-D"), e.g. 2025-W22 or 2025-W22-3
     * @returns A date object representing the passed in date, if the day of week is not defined, it will return monday
     * Note: Thank you, Perplexity.ai ^^
     */
    public isoWeekStringToDate(isoWeekDate: string): Date | null {
        const m = isoWeekDate.match(/^(\d{4})-W(\d{2})(?:-(\d))?$/);
        if (!m) return null;
        const [, year, week, day] = m;
        const wd = day ? +day : 1;
        // Local (not UTC) date parts on purpose: the formatting methods read the local date parts,
        // so a UTC based date would be off by one day for users west of UTC (see #597).
        const jan4 = new Date(+year, 0, 4);
        const weekDate = new Date(jan4);
        weekDate.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7) + (+week - 1) * 7 + (wd - 1));
        return weekDate;
    }

    /**
     * Formats a date given as string or date object to a string using the date utils date format methods
     * depending on the provided interval.
     * @param date The date to be formatted, either a date object or string date representation
     * @param interval an interval: day, week, month, year
     * @param userDateFormat The dateformat of the user (YYYY-MM-DD, DD.MM.YYYY or similar)
     * @returns A string with the formatted date value or an empty string for undefined or invalid date parameter
     */
    public formatDateByInterval(date: Date | string | undefined, interval: string, userDateFormat: string): string {
        if (date === undefined) {
            return '';
        }
        let d: Date;
        if (typeof date === 'string' && String(date).charAt(5) === 'W') {
            const weekDate = this.isoWeekStringToDate(date);
            if (weekDate === null) {
                return '';
            }
            d = weekDate;
        } else {
            d = typeof date === 'string' ? this.parseCalendarDate(date) : new Date(date);
        }
        if (isNaN(d.getTime())) {
            return '';
        }

        switch (interval) {
            case 'day':
                return this.formatDate(d, userDateFormat);
            case 'week':
                return this.formatWeek(d);
            case 'month':
                return this.formatMonth(d, userDateFormat);
            case 'year':
                return this.formatYear(d);
            default:
                return this.formatDate(d, userDateFormat);
        }
    }

    /**
     * Parses a calendar date string ("YYYY", "YYYY-MM" or "YYYY-MM-DD") into a date object at local
     * midnight of that very day. Such values carry no time zone - they name a calendar day, as the
     * report API's "day", "month" and "year" groupings do.
     *
     * new Date('2026-09-02') would interpret the string as UTC midnight instead, which the date
     * formatting methods (they read the local date parts) then render as the previous day for every
     * user west of UTC (see #597). Any other string is left to the standard Date parsing.
     *
     * @param date A date string, e.g. a report group value or a full ISO-8601 date time string
     * @returns A date object; local midnight of the named day for a calendar date string, or an
     *          invalid date when its parts do not name a day that exists (e.g. "2026-13")
     */
    public parseCalendarDate(date: string): Date {
        const m = date.match(/^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/);
        if (!m) {
            return new Date(date);
        }
        const [, year, month, day] = m;
        const monthIndex = month ? +month - 1 : 0;
        const dayOfMonth = day ? +day : 1;
        const parsed = new Date(+year, monthIndex, dayOfMonth);
        // The Date constructor maps the years 0 - 99 into the 20th century and rolls out-of-range
        // parts over into the next month or year, so the year is restored and the result is only
        // accepted when it still names the very day the string did.
        parsed.setFullYear(+year);
        if (parsed.getMonth() !== monthIndex || parsed.getDate() !== dayOfMonth) {
            return new Date(NaN);
        }
        return parsed;
    }

    /**
     * Creates an ISO-8601 string from the provided date object. The string shows the local time
     * (not UTC like Date.toISOString()). It optionally omits the utc offset.
     * @param date The date object to be converted to an ISO-8601 string
     * @param omitOffset (default: false) When set to true the utc offset or Z is omitted.
     * @returns an ISO-8601 string containing the local time without UTC offset
     */
    public getLocalTimeIso(date: Date, omitOffset: boolean = false): string {
        const pad = (num: number): string => num.toString().padStart(2, '0');
        const padMs = (num: number): string => num.toString().padStart(3, '0');

        const dateStr =
            date.getFullYear() +
            '-' +
            pad(date.getMonth() + 1) +
            '-' +
            pad(date.getDate()) +
            'T' +
            pad(date.getHours()) +
            ':' +
            pad(date.getMinutes()) +
            ':' +
            pad(date.getSeconds()) +
            '.' +
            padMs(date.getMilliseconds());

        if (omitOffset) {
            return dateStr;
        }

        const offset = -date.getTimezoneOffset();
        const sign = offset >= 0 ? '+' : '-';
        const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
        const offsetMinutes = pad(Math.abs(offset) % 60);

        const offSetStr = offset === 0 ? 'Z' : sign + offsetHours + ':' + offsetMinutes;

        return dateStr + offSetStr;
    }

    // TODO remove new, unused methods...
    /**
     * This method creates a local time date object, that has the same date and time as the passed in utcDate
     * with the provided utcOffset.
     *
     * This means, that it makes a UTC date object with 2025-06-12T19:31:26.000Z and with an offset of +12:00
     * to a local date object with 2025-06-12T19:31:26.000 as date and time, e.g. 2025-06-12T19:31:26.000+02:00
     * when your computer time is set to central european summer time.
     *
     * This method is needed to convert a
     *
     * @param utcDate
     * @param utcOffset
     * @returns The date object in local time
     */
    public convertDateTimezone(utcDate: Date, utcOffset: string): Date {
        const momentFrom = moment.utc(utcDate).utcOffset(utcOffset);
        const isoStr = momentFrom.toISOString(true);
        const noTzStr = isoStr.substring(0, 19);
        return new Date(noTzStr);
    }

    /**
     * It takes the input date object (e.g. 2025-08-27T19:44:00+02:00) and converts it to a date object with the given
     * UTC offset (e.g. 2025-08-27T19:44:00+11:00).
     *
     * This is needed for proper API calls, because Date objects in the UI are always using the local users date time,
     * but the activity dates on the API and database may have a different timezone set.
     *
     * The date object (input param) of the upper example will be displayed in the UI as "2025-08-27 19:44:00",
     * the resulting date will be displayed differently and is only meant to be used for persisting the object
     * via the API.
     *
     * This operation needs to be performed before updating an activity via the API.
     *
     * @param date The date used in the UI
     * @param originalOffset The original offset of the entity
     * @return A date object that contains the right timestamp for API.
     */
    public getDateForOffset(date: Date, originalOffset: string): Date {
        // Extract wall-clock components from the UI date
        const year = date.getFullYear();
        const month = date.getMonth(); // zero-based
        const day = date.getDate();
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();

        const pad = (n: number): string => String(n).padStart(2, '0');
        const isoString =
            `${year}-${pad(month + 1)}-${pad(day)}T` +
            `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` +
            `${originalOffset}`;

        return new Date(isoString);
    }
}
